import { Drive, GetApp, Huddles, InviteViewModel, Onboarding, Sharing, Shell, Support } from "@bundle/core";
import { FilePlatformContext, I18nProvider, openLink, PrimitivesProvider, SessionContext, Toaster, useOwned, usePreferences, useSession, useT } from "@bundle/ui";
import { lazy, Suspense, useCallback, useEffect, useReducer, useRef, useState } from "react";
import type { Container } from "./platform/container.ts";
import { PreferencesProvider } from "./platform/preferences.tsx";
import { settingsPlatform } from "./platform/settings/index.ts";
import { takeOAuthError, takeTwoFactorMarker } from "./platform/auth.ts";
import { WEB_ORIGIN } from "./platform/env.ts";
import { featureFlags } from "./platform/features.ts";
import { desktopSyncPageFromPath } from "./platform/desktopSync.ts";
import { bookingCodeFromPath, bookingTokenFromPath } from "./platform/booking.ts";
import { isDesktop } from "./platform/desktop.ts";
import { DesktopAuthPage } from "./pages/DesktopAuthPage.tsx";
import { OpenInAppPage } from "./pages/OpenInAppPage.tsx";
import { OAuthPage } from "./pages/OAuthPage.tsx";
import { UNDO_EMAIL_CHANGE_PATH, UndoEmailChangePage } from "./pages/UndoEmailChangePage.tsx";
import { CreateWorkspaceScreen } from "./screens/CreateWorkspaceScreen.tsx";
import { InviteScreen } from "./screens/InviteScreen.tsx";
import { LoadingScreen } from "./screens/LoadingScreen.tsx";
import { SignInScreen } from "./screens/SignInScreen.tsx";
import { SecureAccountScreen, SignupScreen } from "./pages/SignupPage.tsx";
import { goTo, openSignup, SIGNUP_PATH, signupNext } from "./platform/signupRoute.ts";
import { ConsentPrompt } from "./shell/ConsentPrompt.tsx";
import { PlatformContext } from "./shell/PlatformContext.ts";
import { UpdateToast } from "./shell/UpdateToast.tsx";
import { AboutDialog, UpdatedToast } from "./shell/AboutDialog.tsx";
import { FilesToast } from "./shell/FilesToast.tsx";
import { WorkspaceRoot } from "./shell/WorkspaceRoot.tsx";

const here = () => `${location.pathname}${location.search}`;
/** The public meeting page (external huddles), loaded only when a meeting link is opened. */
const MeetingPage = lazy(() => import("./pages/MeetingPage.tsx"));
/** A page published to the web (/s/<token>, featureFlags.sharing), loaded only when such a link is opened. */
const PublicPage = lazy(() => import("./pages/PublicPage.tsx"));
/** A booking page (/book/<code>) or a guest's booking (/booking/<token>): public, loaded only when opened. */
const BookPage = lazy(() => import("./pages/BookingPage.tsx").then((m) => ({ default: m.BookPage })));
const ManageBookingPage = lazy(() => import("./pages/BookingPage.tsx").then((m) => ({ default: m.ManageBookingPage })));
/** A Files item shared with "Anyone with the link" (/f/<token>), loaded only when such a link is opened. */
const PublicDrivePage = lazy(() => import("./pages/PublicDrivePage.tsx"));
/** The desktop sync windows (/{ws}/desktop/sync/…, files-desktop-sync.md): in the desktop app, or a mock build. */
const DesktopSyncPage = lazy(() => import("./pages/desktopSync/DesktopSyncPage.tsx"));
/** The desktop call window (/desktop/ring, src-tauri/src/ring.rs): a huddle ring above every app. */
const DesktopRingPage = lazy(() => import("./pages/desktopRing/DesktopRingPage.tsx"));
/**
 * The primitives gallery (/gallery): dev and mock builds only. Both conditions are build-time constants, so a
 * production build has `null` here and never emits the page's chunk.
 */
const GalleryPage = import.meta.env.DEV || import.meta.env.VITE_MOCK === "1" ? lazy(() => import("./pages/GalleryPage.tsx")) : null;

function InviteRoute({ container, token }: { container: Container; token: string }) {
  const create = useCallback(() => new InviteViewModel(container.api, container.session, token), [container, token]);
  const vm = useOwned(`invite:${token}`, create);
  return <InviteScreen vm={vm} returnTo={`/invite/${token}`} />;
}

/** Top-level routing: desktop sign-in page, then sign in → accept invite → create workspace → workspace. */
function Gate({ container }: { container: Container }) {
  const { vm, state } = useSession();
  const [, rerender] = useReducer((n: number) => n + 1, 0);

  // Startup (once): an invite link in the URL, the result of an OAuth redirect, and the server session (cookie).
  useState(() => {
    const token = Onboarding.inviteTokenFromPath(location.pathname);
    if (token !== undefined) vm.setInviteToken(token);
    const failed = takeOAuthError();
    if (failed !== undefined) vm.reportError(failed);
    // A provider sign-in for an account with two-step verification: the challenge comes next (no session yet).
    if (takeTwoFactorMarker()) vm.showTwoFactor();
    return true;
  });
  useEffect(() => {
    if (vm.state.status === "signedOut") void vm.resume();
    else void vm.loadWorkspaces();
    window.addEventListener("popstate", rerender);
    return () => window.removeEventListener("popstate", rerender);
  }, [vm]);

  // My accent colour and theme follow my account (Settings → Appearance), like Slack's themes; the device keeps the last ones.
  const prefs = usePreferences();
  const userId = state.status === "signedIn" ? state.session?.userId : undefined;
  // Read through a ref: the setters change with every pick, and the account is loaded once per sign-in only.
  const prefsRef = useRef(prefs);
  useEffect(() => {
    prefsRef.current = prefs;
  });
  useEffect(() => {
    if (userId === undefined) return;
    let live = true;
    settingsPlatform(container)
      .api.appearanceSettings()
      .then((s) => {
        if (!live) return;
        prefsRef.current.setAccent(s.accent ?? "indigo");
        prefsRef.current.setTheme(s.theme ?? "system");
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [userId, container]);

  // Leaving the invite page (joined or dismissed): drop /invite/<token> from the address bar.
  useEffect(() => {
    if (state.inviteToken === undefined && location.pathname.startsWith("/invite/")) history.replaceState(null, "", "/");
  }, [state.inviteToken]);

  const screen = vm.screen(Onboarding.workspaceSlugFromPath(location.pathname));
  // Remember the workspace the URL (or the switcher) opened. Not on /desktop-auth: signing in there must come back to
  // that page (and its query), not move the address bar to the workspace.
  const desktopAuth = location.pathname === "/desktop-auth";
  // "Open in app" handoff (/app/open?to=<path>): tries bundle://<path>, signed in or not; never opens a workspace.
  const openInApp = location.pathname === GetApp.HANDOFF_PATH;
  // An app's OAuth install consent (/oauth/authorize): its own page; signing in there comes back to it.
  const oauth = location.pathname === "/oauth/authorize";
  // The undo link in the "sign-in email changed" notice (no session needed; it signs every session out).
  const undoEmail = location.pathname === UNDO_EMAIL_CHANGE_PATH;
  // A meeting link (/m/<code>, external huddles): its own page, signed in or not; it must not open a workspace either.
  const meetingCode = featureFlags.huddles ? Huddles.meetingCodeFromPath(location.pathname) : undefined;
  // A public page link (/s/<token>): readable by anyone, signed in or not; it doesn't open a workspace.
  const publicToken = featureFlags.sharing ? Sharing.publicTokenFromPath(location.pathname) : undefined;
  // A Files "Anyone with the link" link (/f/<token>): the same, for a file or folder.
  const driveToken = Drive.publicDriveTokenFromPath(location.pathname);
  // A booking page (/book/<code>) or a guest's booking (/booking/<token>): no account; it doesn't open a workspace.
  const bookCode = bookingCodeFromPath(location.pathname);
  const bookingToken = bookingTokenFromPath(location.pathname);
  const opened =
    screen.screen === "workspace" && location.pathname !== SIGNUP_PATH && !desktopAuth && !openInApp && !oauth && !undoEmail && meetingCode === undefined && publicToken === undefined && driveToken === undefined && bookCode === undefined && bookingToken === undefined
      ? screen.workspace
      : undefined;
  useEffect(() => {
    if (opened !== undefined && vm.state.session?.workspace?.id !== opened.id) vm.openWorkspace(opened);
  }, [opened, vm]);
  // Analytics screen_view for the screens outside a workspace (WorkspaceRoot reports its tabs).
  const outside = desktopAuth ? "desktop_auth" : meetingCode !== undefined ? "meeting" : screen.screen === "workspace" || screen.screen === "loading" ? undefined : screen.screen;
  useEffect(() => {
    if (outside !== undefined) container.analytics.screen(outside);
  }, [outside, container]);

  // Create account: /signup signed out; "Secure your account" right after its code (wherever the address points).
  const signupPage = location.pathname === SIGNUP_PATH;
  useEffect(() => {
    if (signupPage && state.status === "signedIn" && state.signup?.step !== "secure") goTo(signupNext(location.search), true);
  }, [signupPage, state.status, state.signup]);
  if (state.status === "signedIn" && state.signup?.step === "secure") return <SecureAccountScreen container={container} />;
  if (signupPage) return state.status === "signingIn" && state.busy === undefined ? <LoadingScreen /> : <SignupScreen container={container} />;
  if (desktopAuth) return <DesktopAuthPage container={container} />;
  if (openInApp) return <OpenInAppPage />;
  if (oauth) return <OAuthPage container={container} />;
  if (undoEmail) {
    const toSignIn = () => {
      if (vm.state.status === "signedIn") vm.signOut();
      history.replaceState(null, "", "/");
      rerender();
    };
    return <UndoEmailChangePage onSignIn={toSignIn} />;
  }
  if (publicToken !== undefined) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <PublicPage container={container} token={publicToken} />
      </Suspense>
    );
  }
  if (bookCode !== undefined || bookingToken !== undefined) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        {bookCode !== undefined ? <BookPage container={container} code={bookCode} /> : <ManageBookingPage container={container} token={bookingToken ?? ""} />}
      </Suspense>
    );
  }
  if (driveToken !== undefined) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <PublicDrivePage container={container} token={driveToken} />
      </Suspense>
    );
  }
  if (state.status === "signingIn" && state.busy === undefined) return <LoadingScreen />;
  if (meetingCode !== undefined) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <MeetingPage container={container} code={meetingCode} />
      </Suspense>
    );
  }
  switch (screen.screen) {
    case "signIn":
      // A shared file link opened signed out: no file details before sign-in, then back to the file.
      if (Shell.parseUrl(location.pathname)?.type === "file") return <FileSignIn container={container} />;
      return <SignInScreen returnTo={here()} mock={container.mock} defaultDevEmail={container.mock ? "ada@acme.test" : ""} onCreateAccount={() => openSignup(vm, here())} />;
    case "invite":
      return <InviteRoute container={container} token={screen.token} />;
    case "loading":
      return <LoadingScreen />;
    case "createWorkspace":
      return <CreateWorkspaceScreen addressPrefix={`${new URL(WEB_ORIGIN).host}/`} />;
    case "workspace": {
      // The desktop sync windows: chrome-less, no workspace shell (and no socket).
      const syncPage = isDesktop() || container.mock ? desktopSyncPageFromPath(location.pathname) : undefined;
      if (syncPage !== undefined)
        return (
          <Suspense fallback={<LoadingScreen />}>
            <DesktopSyncPage container={container} workspace={screen.workspace} page={syncPage} />
          </Suspense>
        );
      return (
        <WorkspaceRoot
          key={`${state.session?.userId ?? ""}:${screen.workspace.id}`}
          container={container}
          userId={state.session?.userId ?? ""}
          workspace={screen.workspace}
        />
      );
    }
  }
}

/** Signed-out visitor on /{ws}/f/{fileId}: "Sign in to view this file" (never the file's name or details). */
function FileSignIn({ container }: { container: Container }) {
  const t = useT();
  const { vm } = useSession();
  return (
    <SignInScreen
      returnTo={here()}
      mock={container.mock}
      defaultDevEmail={container.mock ? "ada@acme.test" : ""}
      title={t("files.page.signIn.title")}
      subtitle={t("files.page.signIn.body")}
      onCreateAccount={() => openSignup(vm, here())}
    />
  );
}

/** Files dropped outside a drop zone must not navigate away from the app (the browser would open them). */
function useDropGuard(): void {
  useEffect(() => {
    const guard = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes("Files") === true && !e.defaultPrevented) e.preventDefault();
    };
    window.addEventListener("dragover", guard);
    window.addEventListener("drop", guard);
    return () => {
      window.removeEventListener("dragover", guard);
      window.removeEventListener("drop", guard);
    };
  }, []);
}

/** Where every toast shows (bottom right, stacked; packages/ui Toast). */
function AppToaster() {
  const t = useT();
  return <Toaster label={t("toast.region")} />;
}

export function App({ container }: { container: Container }) {
  useDropGuard();
  if (GalleryPage !== null && location.pathname === "/gallery") {
    // Outside the preferences (the gallery picks its own theme) and the session (nothing signs in).
    const locale = new URLSearchParams(location.search).get("locale") === "pt-BR" ? "pt-BR" : "en";
    return (
      <PrimitivesProvider media={container.media}>
        <I18nProvider locale={locale}>
          <Suspense fallback={<LoadingScreen />}>
            <GalleryPage />
          </Suspense>
          <AppToaster />
        </I18nProvider>
      </PrimitivesProvider>
    );
  }
  if (isDesktop() && location.pathname === "/desktop/ring") {
    // The call window: no session, shell or toasts of its own (the main window drives it).
    return (
      <PrimitivesProvider media={container.media}>
        <PreferencesProvider storage={container.storage}>
          {(locale) => (
            <I18nProvider locale={locale}>
              <Suspense fallback={null}>
                <DesktopRingPage />
              </Suspense>
            </I18nProvider>
          )}
        </PreferencesProvider>
      </PrimitivesProvider>
    );
  }
  return (
    <PrimitivesProvider media={container.media}>
      <PreferencesProvider storage={container.storage}>
        {(locale) => (
          <I18nProvider locale={locale}>
            <SessionContext.Provider value={container.session}>
              <PlatformContext.Provider value={container}>
                <FilePlatformContext.Provider value={container.files}>
                  <Gate container={container} />
                  {/* Desktop: lets the window be dragged by its top edge on screens without the shell's title bar (shell.css). */}
                  <div className="desktop-drag-strip" data-tauri-drag-region="" aria-hidden="true" />
                  <UpdateToast />
                  <UpdatedToast onWhatsNew={() => openLink(Support.whatsNewUrl(locale))} />
                  <AboutDialog onWhatsNew={() => openLink(Support.whatsNewUrl(locale))} />
                  <ConsentPrompt />
                  <FilesToast files={container.files} />
                  <AppToaster />
                </FilePlatformContext.Provider>
              </PlatformContext.Provider>
            </SessionContext.Provider>
          </I18nProvider>
        )}
      </PreferencesProvider>
    </PrimitivesProvider>
  );
}
